import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ApiService from '../network/API';

const DEFAULT_IMAGE = 'danbel/lab-base:1';

// Настройка окружений лабораторной задачи (преподаватель): имя, образ контейнера и контрольный скрипт.
// Скрипт запускается внутри контейнера студента при проверке и печатает факты о состоянии системы —
// по ним и критериям задачи AI выставляет баллы. Уже запущенные окружения студентов не меняются.
const LabEnvironmentsDialog = ({ open, onClose, problemId }) => {
    const [items, setItems] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setItems(null);
        setError(null);
        ApiService.getLabEnvironments(problemId)
            .then((list) => setItems(list.map((e) => ({ ...e, checkScript: e.checkScript || '' }))))
            .catch((e) => setError(e.reason || e.message || 'Не удалось загрузить окружения'));
    }, [open, problemId]);

    const update = (index, patch) => setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await ApiService.saveLabEnvironments(problemId, items);
            onClose();
        } catch (e) {
            setError(e.reason || e.message || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Окружения лабораторной</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!items && !error && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
                {items && (
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Новые настройки получат только новые запуски окружений. Базовый образ с редакторами и сетевыми
                            утилитами — <b>{DEFAULT_IMAGE}</b>; серверное ПО студент ставит сам.
                        </Alert>
                        {items.map((item, index) => (
                            <Box key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <TextField label="Имя окружения" size="small" value={item.name}
                                               onChange={(e) => update(index, { name: e.target.value })} sx={{ flex: 1 }}
                                               helperText="латиница, цифры, «-»" />
                                    <TextField label="Образ Docker" size="small" value={item.dockerImage}
                                               onChange={(e) => update(index, { dockerImage: e.target.value })} sx={{ flex: 1 }} />
                                    <IconButton color="error" onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                                                disabled={items.length < 2}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Stack>
                                <TextField
                                    label="Контрольный скрипт (bash, выполняется в контейнере студента при проверке)"
                                    value={item.checkScript}
                                    onChange={(e) => update(index, { checkScript: e.target.value })}
                                    multiline minRows={6} maxRows={20} fullWidth
                                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 }, spellCheck: false } }}
                                    helperText="Печатайте только факты: конфиги без комментариев, адреса, процессы, коды возврата. Без времени и случайных значений."
                                />
                            </Box>
                        ))}
                        <Box>
                            <Button startIcon={<AddIcon />} onClick={() => setItems((prev) => [...prev, { name: '', dockerImage: DEFAULT_IMAGE, checkScript: '' }])}>
                                Добавить окружение
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Критерии оценки лабы настраиваются на странице «Критерии оценки».
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button variant="contained" onClick={save} disabled={!items || saving}>Сохранить</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LabEnvironmentsDialog;
